import { ThemeKey } from '../../modules/users/components/onboarding/Text';

export const nullFn = (): null => null;
export type Value = string | number | boolean | undefined | null;

export function generateUID() {
  const first = (Math.random() * 46656) | 0;
  const second = (Math.random() * 46656) | 0;
  return (
    ('000' + first.toString(36)).slice(-3) +
    ('000' + second.toString(36)).slice(-3)
  );
}

export function classNames(...args: Value[]): string {
  const classList = [];

  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    if (!arg) continue;

    const argType = typeof arg;

    if (argType === 'string' || argType === 'number') {
      classList.push(arg);
    } else if (Array.isArray(arg)) {
      if (arg.length) {
        const inner = classNames(...arg);
        if (inner) {
          classList.push(inner);
        }
      }
    }
  }

  return classList.join(' ');
}

const DATEFORMATS: Record<string, Intl.DateTimeFormatOptions> = {
  small: { year: '2-digit', month: 'numeric', day: 'numeric' },
  medium: { year: 'numeric', month: '2-digit', day: '2-digit' },
  large: { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' },
  custom: { month: 'short', day: 'numeric', year: 'numeric' },
};
type FormatStyle = 'small' | 'medium' | 'large' | 'custom';

export function formatDateForViewModel(
  date?: Date | string | null,
  opts?: Intl.DateTimeFormatOptions,
  formatStyle: FormatStyle = 'medium',
  locale = 'en-US',
  convert = true,
) {
  if (date) {
    try {
      const options = opts
        ? opts
        : DATEFORMATS[formatStyle] || DATEFORMATS.medium;
      const value =
        typeof date === 'string' ? new Date(Date.parse(date)) : date;
      const offset = convert
        ? new Date(value.getTime() + value.getTimezoneOffset() * 60000)
        : new Date(value);
      return new Intl.DateTimeFormat(locale, options).format(offset);
    } catch (err) {
      return '';
    }
  }
  return '';
}

const TIMEFORMATS: Record<string, Intl.DateTimeFormatOptions> = {
  short: { timeStyle: 'short' },
  medium: { timeStyle: 'medium' },
  long: { timeStyle: 'long' },
  default: { timeStyle: 'full' },
};
type TimeFormatStyle = 'short' | 'medium' | 'long' | 'default';

export function formatTimeForViewModel(
  date?: Date | string | null,
  formatStyle: TimeFormatStyle = 'default',
  locale = 'en-US',
): string {
  if (date) {
    try {
      const options = TIMEFORMATS[formatStyle];
      const value = typeof date === 'string' ? new Date(date) : date;
      return new Intl.DateTimeFormat(locale, options).format(value);
    } catch (err) {
      return '';
    }
  }

  return '';
}

export function elapsedTimeSince(date?: Date | string | null): string {
  if (date) {
    const d = typeof date === 'string' ? new Date(date) : date;
    const seconds = Math.floor((new Date().getTime() - d.getTime()) / 1000);
    let interval: number;

    interval = seconds / 31536000;
    if (interval > 1) {
      return `${Math.floor(interval)} year${
        Math.floor(interval) > 1 ? 's' : ''
      }`;
    }

    interval = seconds / 2592000;
    if (interval > 1) {
      return `${Math.floor(interval)} month${
        Math.floor(interval) > 1 ? 's' : ''
      }`;
    }
    interval = seconds / 86400;
    if (interval > 1) {
      return `${Math.floor(interval)} day${
        Math.floor(interval) > 1 ? 's' : ''
      }`;
    }

    return `Today`;
  }

  return '';
}

export function numberSpacingCorrection(
  value?: number | string | null,
): string {
  if (!isNumber(value)) {
    return '0';
  }

  return value ? value.toLocaleString() : '0';
}

export function toSeperatedNumber(value: number, seperator = ',') {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, seperator);
}

export function isNumber(value?: string | number | null): boolean {
  if (typeof value === 'number') return true;

  const NUMBER_REGEX = /^[0-9]{0,8}$/;

  if (!value) return false;
  return NUMBER_REGEX.test(value) && value !== '';
}

export function pluralString(value: string, count: number): string {
  if (count > 1 || count === 0) {
    return `${value}s`;
  }
  return value;
}

export function stripTimeFromDate(
  date: Date,
  UTC = false,
  locale = 'UTC',
  format: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  },
): string {
  const d = new Date(
    UTC ? date.getUTCFullYear() : date.getFullYear(),
    UTC ? date.getUTCMonth() : date.getMonth(),
    UTC ? date.getUTCDate() : date.getDate(),
  );

  return Intl.DateTimeFormat(locale, format).format(d);
}

export function getMonthFromDate(d: Date, locale = 'UTC'): string {
  return Intl.DateTimeFormat(locale, { month: 'short' }).format(d);
}

export function getFotmatedTimeFromDate(d: Date, utc = false): string {
  const hours = utc ? d.getUTCHours() : d.getHours();
  const minutes = utc ? d.getUTCMinutes() : d.getMinutes();

  const newFromat = hours > 12 ? 'PM' : 'AM';
  const adjHours = hours % 12;
  const strHours = adjHours ? `${adjHours}` : '12';
  const strMin = minutes < 10 ? `0${minutes}` : `${minutes}`;

  return `${strHours}:${strMin} ${newFromat}`;
}

export function addEllipsisAboveCertainLength(
  string: string,
  length: number,
): string {
  if (string.length > length) {
    return string.substring(0, length).trim() + '...';
  }
  return string;
}

export function percentageCalc(top: number, bottom: number): number {
  return Number(((top / bottom) * 100).toFixed(2));
}

export function colorByPercentage(value: number): ThemeKey {
  switch (true) {
    case value <= 10:
      return 'RED';
    case value > 10 && value <= 20:
      return 'PURPLE';
    case value > 20 && value <= 30:
      return 'ORANGE';
    case value > 30 && value <= 40:
      return 'BLUE';
    case value > 40:
      return 'GREEN';
    default:
      return 'RED';
  }
}
